import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listEmailSuppressions,
  addEmailSuppression,
  removeEmailSuppression,
  SuppressionRow,
} from '../api/client';
import EmailNav from '../components/EmailNav';
import './EmailSuppressions.css';

/**
 * Suppressions — manages the per-club email suppression list.
 *
 * Renamed from EmailSubscribers in 2026-05-29 (Email v2 Phase 1):
 * that name was misleading — "subscribers" implies people who opted
 * IN, but this is the list of people we WON'T send to. The new
 * EmailSubscribers page holds the real opt-in list.
 *
 * The suppression list is the inverse of a subscriber list. Four
 * ways a row lands here:
 *   - User clicked unsubscribe in an email  (reason: 'unsubscribe')
 *   - SES hard-bounced their address        (reason: 'bounce')
 *   - SES received a spam complaint         (reason: 'complaint')
 *   - Admin added them manually             (reason: 'manual')
 *
 * Admin can re-add (un-suppress) anyone — useful if a parent
 * accidentally clicks unsubscribe and asks the club to put them
 * back on. Removing a bounce/complaint suppression is admin's
 * choice and own risk (re-sending to a previously bounced address
 * dings your sender reputation).
 */
export default function EmailSuppressions() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SuppressionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [reasonFilter, setReasonFilter] = useState<'all' | 'unsubscribe' | 'bounce' | 'complaint' | 'manual'>('all');

  // Manual-add form state
  const [addEmail, setAddEmail] = useState('');
  const [addReason, setAddReason] = useState('');
  const [adding, setAdding] = useState(false);

  // Per-row "are you sure?" state. Holds the email currently mid-confirm
  // so we render an inline confirm button instead of immediate-deleting.
  const [confirmRemoveEmail, setConfirmRemoveEmail] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  useEffect(() => {
    refresh();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  function refresh() {
    setLoading(true);
    listEmailSuppressions()
      .then(data => setRows(data.suppressions || []))
      .catch(err => {
        if (err instanceof Error && err.message.includes('not available')) {
          navigate('/dashboard');
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load suppression list');
      })
      .finally(() => setLoading(false));
  }

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    setError('');
    const email = addEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }
    setAdding(true);
    try {
      await addEmailSuppression(email, addReason || 'Added manually by admin');
      setAddEmail('');
      setAddReason('');
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add');
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(email: string) {
    setError('');
    setRemoving(email);
    try {
      await removeEmailSuppression(email);
      setConfirmRemoveEmail(null);
      // Optimistic local update so the row vanishes immediately rather than
      // waiting for the refetch round-trip.
      setRows(prev => prev.filter(r => r.email !== email));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove');
    } finally {
      setRemoving(null);
    }
  }

  // Filter
  const filtered = rows.filter(r => {
    if (reasonFilter !== 'all' && r.reason !== reasonFilter) return false;
    if (filter && !r.email.toLowerCase().includes(filter.toLowerCase())) return false;
    return true;
  });

  // Stats
  const counts = rows.reduce<Record<string, number>>((acc, r) => {
    acc[r.reason] = (acc[r.reason] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="email-page-shell">
      <EmailNav />

      <div className="page-head">
        <div>
          <h1>Suppressions</h1>
          <p className="subtitle">
            Your "do not email" list. People who unsubscribed, whose addresses bounced,
            or who marked your emails as spam. Re-add anyone who landed here by accident.
          </p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Stats summary */}
      {!loading && (
        <div className="subs-stats">
          <div className="stat-card">
            <div className="stat-label">Total suppressed</div>
            <div className="stat-value">{rows.length}</div>
            <div className="stat-sub">won't receive emails</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Unsubscribed</div>
            <div className="stat-value">{counts.unsubscribe || 0}</div>
            <div className="stat-sub">clicked the link</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Bounced</div>
            <div className="stat-value stat-warn">{counts.bounce || 0}</div>
            <div className="stat-sub">address invalid</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Complaints + manual</div>
            <div className="stat-value">{(counts.complaint || 0) + (counts.manual || 0)}</div>
            <div className="stat-sub">marked as spam or added by you</div>
          </div>
        </div>
      )}

      {/* Manual add form */}
      <details className="manual-add">
        <summary>+ Add an email to the suppression list manually</summary>
        <form onSubmit={handleAdd} className="manual-add-form">
          <input
            type="email"
            className="form-input"
            value={addEmail}
            onChange={e => setAddEmail(e.target.value)}
            placeholder="someone@example.com"
            required
          />
          <input
            type="text"
            className="form-input"
            value={addReason}
            onChange={e => setAddReason(e.target.value)}
            placeholder="Reason (optional, e.g. 'asked to stop receiving emails')"
          />
          <button type="submit" className="btn btn-primary" disabled={adding}>
            {adding ? 'Adding…' : 'Add'}
          </button>
        </form>
        <p className="manual-add-hint">
          Use this if someone asked verbally / by text to be removed. They won't receive future emails.
        </p>
      </details>

      {/* Filter bar */}
      <div className="subs-filter-bar">
        <input
          type="search"
          className="form-input"
          placeholder="Search by email address…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
        <select
          className="form-input"
          value={reasonFilter}
          onChange={e => setReasonFilter(e.target.value as any)}
        >
          <option value="all">All reasons</option>
          <option value="unsubscribe">Unsubscribed</option>
          <option value="bounce">Bounced</option>
          <option value="complaint">Complaint</option>
          <option value="manual">Manual</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📭</div>
          <h3>{rows.length === 0 ? 'No suppressions' : 'No matches'}</h3>
          <p>
            {rows.length === 0
              ? 'Nobody has unsubscribed or bounced yet — your list is clean.'
              : 'Try a different filter or clear the search.'}
          </p>
        </div>
      ) : (
        <div className="subs-table-wrap">
          <table className="subs-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Reason</th>
                <th>Added</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => (
                <tr key={row.email}>
                  <td className="cell-email">{row.email}</td>
                  <td>
                    <span className={`reason-pill reason-${row.reason}`}>
                      {row.reason === 'unsubscribe' && '👋 Unsubscribed'}
                      {row.reason === 'bounce' && '⚠️ Bounced'}
                      {row.reason === 'complaint' && '🚫 Complaint'}
                      {row.reason === 'manual' && '✋ Manual'}
                    </span>
                    {row.reasonDetail && (
                      <div className="reason-detail" title={row.reasonDetail}>
                        {row.reasonDetail.length > 60
                          ? row.reasonDetail.slice(0, 60) + '…'
                          : row.reasonDetail}
                      </div>
                    )}
                  </td>
                  <td className="cell-date">{formatDate(row.addedAt)}</td>
                  <td className="cell-action">
                    {confirmRemoveEmail === row.email ? (
                      <div className="confirm-cluster">
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRemove(row.email)}
                          disabled={removing === row.email}
                        >
                          {removing === row.email ? 'Removing…' : 'Yes, re-add'}
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setConfirmRemoveEmail(null)}
                          disabled={removing === row.email}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setConfirmRemoveEmail(row.email)}
                        title={
                          row.reason === 'bounce'
                            ? 'Re-adding a bounced address may damage your sender reputation if it bounces again'
                            : 'Re-add this email to your sendable list'
                        }
                      >
                        Re-add
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length >= 200 && (
            <p className="list-note">
              Showing first 200 entries. Use the search to narrow down.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: sameYear ? undefined : 'numeric',
  });
}
